<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Configurações de Evento" :on-add="() => $router.push({ name: 'create-event-configuration' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="table"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.active`]="{ item }">
            <span> {{ item.active ? 'Sim' : 'Não' }}</span>
          </template>
          <template #[`item.created_at`]="{ item }">
            <span>
              {{ DateTime.fromISO(item.created_at).toFormat("dd/MM/yyyy HH:mm:ss") }}
            </span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { DataTableFilter, DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import EventConfiguration from '@/types/EventConfiguration'
import { Resources } from '@/plugins/permissions/typing'
import TPageTitle from '@/components/TPageTitle.vue'
import DataTable from '@/components/datatable/DataTable.vue'
import EventConfigurationService from '@/services/EventConfigurationService'
import { DateTime } from 'luxon'
import { useRouter } from 'vue-router'
import { useAbility } from '@casl/vue'
import { useLoader } from '@/stores/loader'
import { useDialog } from '@/stores/dialog'
import { useSnackBar } from '@/stores/snackbar'
import { ref, useTemplateRef } from 'vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'

const resource: Resources = 'event-configurations'

const router = useRouter()
const loader = useLoader()
const dialog = useDialog()
const snackbar = useSnackBar()

const { can } = useAbility()

const service = new EventConfigurationService()

const table = useTemplateRef('table')

const headers = ref<DataTableHeader[]>([
  { key: 'id', value: 'id', title: 'ID', sortable: false },
  { key: 'name', value: 'name', title: 'Nome', sortable: false },
  { key: 'type_name', value: 'type_name', title: 'Tipo de eventos', sortable: false },
  { key: 'active', value: 'active', title: 'Ativo', sortable: false },
  { key: 'issuer', value: 'issuer', title: 'Órgão Emitente', sortable: false },
  { key: 'mapping.local.name', value: 'mapping.local.name', title: 'Local', sortable: false },
  { key: 'created_at', value: 'created_at', title: 'Criado em', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
])

const filters = ref<DataTableFilter[]>([
  useTextFilter({ field: 'name', label: 'Nome', icon: 'fa-solid fa-signature', required: false }),
  useSelectFilter({
    field: 'type',
    label: 'Tipo de eventos',
    icon: 'fa-solid fa-list',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Eventos Esportivos', value: '1' },
      { title: 'Eventos Sociais', value: '2' },
    ],
    required: false,
  }),
  useTextFilter({ field: 'issuer', label: 'Órgão Emitente', icon: 'fa-solid fa-landmark', required: false }),
  useSelectFilter({
    field: 'active',
    label: 'Status',
    icon: 'fa-solid fa-list',
    initial: 'true',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Ativo', value: 'true' },
      { title: 'Inativo', value: 'false' },
    ],
    required: false,
  }),
])

const actions: DefaultDataTableActions = {
  update: {
    callback: (item: EventConfiguration) => router.push({ name: 'edit-event-configuration', params: { id: item.id } }),
    condition: () => can('update', 'event-configurations'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: EventConfiguration) => destroy(item),
    condition: () => can('update', 'event-configurations'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    loader.increment()
    return await service.index(page, filters, per_page)
  } finally {
    loader.decrement()
  }
}

async function destroy(model: EventConfiguration) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir esta configuração? Essa ação é irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()
    await service.destroy(model)
    table.value?.search()
    snackbar.show('Configuração excluída com sucesso.', 'success')
  } finally {
    loader.decrement()
  }
}

</script>
